import { errorMessage, titleColorHex } from "@atlas/shared";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import {
  Award,
  Check,
  Clock,
  CloudRain,
  Coffee,
  CreditCard,
  Eye,
  Flame,
  Flashlight,
  Gift,
  Heart,
  Image as ImageIcon,
  Lock,
  Medal,
  MountainSnow,
  Package,
  ShoppingBag,
  Star,
  ThumbsUp,
  Triangle,
  Trophy,
  X,
  Zap,
} from "lucide-react";
import {
  createStoreCheckout,
  getStoreItems,
  getWallet,
  purchaseItem,
  type StoreItem,
  type StoreItemKind,
  type StoreItemRarity,
  type WalletResponse,
} from "../../api/store";
import { fetchProfileStats, type EquippedCosmeticItem } from "../../api/profile";
import { useAuth } from "../../hooks/useAuth";
import { AtlasPanel } from "../../components/AtlasPanel";
import { Badge } from "../../components/Badge";
import { BetaGateModal } from "../../components/BetaGateModal";
import { useBetaGate } from "../../hooks/useFeatureFlags";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ProfileIdentity } from "../../components/ProfileIdentity";
import { Skel } from "../../components/Skeleton";
import { MAP_COLORS } from "../../components/map/map-colors";
import {
  createMarkerImage,
  LANDMARK_GLYPHS,
  LANDMARK_MARKER_PX,
  PEAK_GLYPH,
  PEAK_MARKER_PX,
  type MarkerGlyph,
} from "../../components/map/AtlasMap";
import { getBadgeAsset, getBannerAsset, getFrameAsset, getStampAsset } from "../../lib/cosmetics";
import { rarityColor, rarityGlowMd, rarityLabel, RARITY_OPTIONS } from "../../lib/rarity";

// ─── Scoped styles ────────────────────────────────────────────────────────────

const CSS = `
.atlas-store-grid{
  display:grid; grid-template-columns:repeat(auto-fill, minmax(220px, 1fr)); gap:12px;
}
.atlas-store-tile{
  display:flex; flex-direction:column; align-items:stretch; gap:0; text-align:left;
  background:var(--surface-card); border:1px solid var(--border-subtle);
  border-radius:var(--radius-lg); box-shadow:var(--shadow-sm), var(--ring-top);
  padding:0; cursor:pointer; overflow:hidden; font-family:var(--font-sans);
  transition:transform var(--dur-quick) var(--ease-out), border-color var(--dur-quick) var(--ease-out), box-shadow var(--dur-quick) var(--ease-out);
}
.atlas-store-tile:hover{ transform:translateY(-2px); border-color:var(--border-default); box-shadow:var(--shadow-md), var(--ring-top); }
.atlas-store-tile--featured{ border-color:var(--border-gold); background:linear-gradient(180deg, var(--accent-soft), transparent 65%), var(--surface-card); }
.atlas-store-tile--locked .atlas-store-tile__preview{ filter:grayscale(1); opacity:.55; }
.atlas-store-tile--uncommon{ box-shadow:var(--shadow-sm), var(--ring-top), var(--glow-rarity-uncommon-sm); }
.atlas-store-tile--uncommon:hover{ box-shadow:var(--shadow-md), var(--ring-top), var(--glow-rarity-uncommon-md); }
.atlas-store-tile--rare{ box-shadow:var(--shadow-sm), var(--ring-top), var(--glow-rarity-rare-sm); }
.atlas-store-tile--rare:hover{ box-shadow:var(--shadow-md), var(--ring-top), var(--glow-rarity-rare-md); }
.atlas-store-tile--epic{ box-shadow:var(--shadow-sm), var(--ring-top), var(--glow-rarity-epic-sm); }
.atlas-store-tile--epic:hover{ box-shadow:var(--shadow-md), var(--ring-top), var(--glow-rarity-epic-md); }
.atlas-store-tile--legendary{ box-shadow:var(--shadow-sm), var(--ring-top), var(--glow-rarity-legendary-sm); }
.atlas-store-tile--legendary:hover{ box-shadow:var(--shadow-md), var(--ring-top), var(--glow-rarity-legendary-md); }
.atlas-store-tile__preview{
  height:104px; display:flex; align-items:center; justify-content:center;
  background:var(--surface-sunken); border-bottom:1px solid var(--border-subtle);
  padding:12px; overflow:hidden;
}
@keyframes atlas-store-modal-in{ from{ opacity:0; transform:translateY(8px); } to{ opacity:1; transform:none; } }
.atlas-store-modal{ animation:atlas-store-modal-in var(--dur-normal) var(--ease-out); }
@media (prefers-reduced-motion: reduce){
  .atlas-store-tile{ transition:none; }
  .atlas-store-tile:hover{ transform:none; }
  .atlas-store-modal{ animation:none; }
}
`;

function ensureCss() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("atlas-store-css")) {
    const s = document.createElement("style");
    s.id = "atlas-store-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) => n.toLocaleString("en-GB");
const gbp = (pence: number) => `£${(pence / 100).toFixed(2)}`;

const KIND_LABEL: Record<StoreItemKind, string> = {
  badge: "Badge",
  banner: "Banner",
  title: "Title",
  map_theme: "Map theme",
  award: "Award",
  physical: "Merch",
  bundle: "Bundle",
  frame: "Avatar frame",
  trace_style: "Trace style",
  stamp: "Check-in stamp",
  pin: "Map pin",
};

const FILTERS: { id: "all" | "map_kinds" | StoreItemKind; label: string }[] = [
  { id: "all", label: "All" },
  { id: "badge", label: "Badges" },
  { id: "banner", label: "Banners" },
  { id: "title", label: "Titles" },
  { id: "frame", label: "Frames" },
  { id: "map_kinds", label: "Map" },
  { id: "stamp", label: "Stamps" },
  { id: "award", label: "Awards" },
  { id: "physical", label: "Merch" },
];

// The "Map" chip groups everything that restyles the activity map.
const MAP_KINDS: StoreItemKind[] = ["map_theme", "trace_style", "pin"];

// Grid sort order: group by kind (mirrors the filter-chip order, keeping the
// map kinds adjacent), then by rarity within a kind. Anything unlisted sinks
// to the end.
const KIND_ORDER: StoreItemKind[] = [
  "badge",
  "banner",
  "title",
  "frame",
  "map_theme",
  "trace_style",
  "pin",
  "stamp",
  "award",
  "physical",
  "bundle",
];
const kindRank = (kind: StoreItemKind) => {
  const i = KIND_ORDER.indexOf(kind);
  return i === -1 ? KIND_ORDER.length : i;
};
const rarityRank = (rarity: StoreItemRarity) => {
  const i = RARITY_OPTIONS.indexOf(rarity);
  return i === -1 ? RARITY_OPTIONS.length : i;
};

function titleColor(token: string | undefined, hex?: string): string {
  switch (token) {
    case "sky":
      return "var(--sky-400)";
    case "spruce":
      return "var(--spruce-400)";
    case "ember":
      return "var(--coral-400)";
    case "proof":
      return "var(--proof-400)";
    default:
      return titleColorHex(hex) ?? "var(--gold-400)";
  }
}

function awardIcon(name: string | undefined, size: number): React.ReactNode {
  const p = { size };
  switch (name) {
    case "heart": return <Heart {...p} />;
    case "star": return <Star {...p} />;
    case "medal": return <Medal {...p} />;
    case "gift": return <Gift {...p} />;
    case "thumbs-up": return <ThumbsUp {...p} />;
    case "flame": return <Flame {...p} />;
    case "mountain": return <MountainSnow {...p} />;
    case "trophy": return <Trophy {...p} />;
    case "triangle": return <Triangle {...p} />;
    case "coffee": return <Coffee {...p} />;
    case "flashlight": return <Flashlight {...p} />;
    case "cloud-rain": return <CloudRain {...p} />;
    default: return <Award {...p} />;
  }
}

function leavesStoreCopy(availableUntil: string | null): string | null {
  if (!availableUntil) return null;
  const date = new Date(availableUntil);
  if (Number.isNaN(date.getTime())) return null;
  return `Leaves the store ${date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
}

function prettySlug(slug: string): string {
  const words = slug.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// ─── Waypoints price mark ─────────────────────────────────────────────────────

function WaypointsPrice({
  amount,
  size = 12,
  color = "var(--gold-400)",
}: {
  amount: number;
  size?: number;
  color?: string;
}) {
  return (
    <span
      className="tnum"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontFamily: "var(--font-mono)",
        fontSize: size,
        fontWeight: 700,
        color,
      }}
    >
      <Triangle size={size - 1} fill="currentColor" aria-hidden="true" />
      {fmt(amount)}
    </span>
  );
}

// ─── Item previews ────────────────────────────────────────────────────────────

function MapThemeSwatch({ item, height = 36 }: { item: StoreItem; height?: number }) {
  const ramp = item.metadata.heatRamp ?? [];
  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          display: "flex",
          height,
          borderRadius: "var(--radius-sm)",
          overflow: "hidden",
          border: "1px solid var(--border-subtle)",
        }}
      >
        {ramp.map((c, i) => (
          <span key={i} style={{ flex: 1, background: c }} />
        ))}
      </div>
      {item.metadata.traceColor && (
        <div
          style={{
            height: 3,
            borderRadius: "var(--radius-pill)",
            background: item.metadata.traceColor,
          }}
        />
      )}
    </div>
  );
}

function ItemPreview({ item, large = false }: { item: StoreItem; large?: boolean }) {
  const m = item.metadata;
  switch (item.kind) {
    case "badge": {
      const src = getBadgeAsset(m.assetKey);
      const s = large ? 96 : 60;
      return src ? (
        <img src={src} alt="" width={s} height={s} style={{ borderRadius: "50%" }} />
      ) : (
        <Award size={large ? 40 : 28} style={{ color: "var(--gold-400)" }} />
      );
    }
    case "banner": {
      const src = getBannerAsset(m.assetKey);
      return src ? (
        <img
          src={src}
          alt=""
          style={{
            width: "100%",
            height: large ? 120 : 76,
            objectFit: "cover",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-subtle)",
          }}
        />
      ) : (
        <ImageIcon size={large ? 40 : 28} style={{ color: "var(--text-faint)" }} />
      );
    }
    case "title":
      return (
        <span
          className="eyebrow"
          style={{
            fontSize: large ? 15 : 12,
            letterSpacing: "0.14em",
            color: titleColor(m.colorToken, m.colorHex),
            textAlign: "center",
          }}
        >
          {m.text ?? item.name}
        </span>
      );
    case "map_theme":
      return <MapThemeSwatch item={item} height={large ? 48 : 34} />;
    case "award":
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 7,
            color: "var(--gold-400)",
          }}
        >
          {awardIcon(m.icon, large ? 40 : 28)}
          {m.packSize != null && (
            <span
              className="eyebrow tnum"
              style={{ fontSize: large ? 11 : 9.5 }}
            >
              Pack of {m.packSize}
            </span>
          )}
        </div>
      );
    case "frame": {
      const src = getFrameAsset(m.assetKey);
      const s = large ? 96 : 60;
      // Preview the ring around a placeholder avatar disc.
      return (
        <span style={{ position: "relative", width: s, height: s, display: "inline-block" }}>
          <span
            style={{
              position: "absolute",
              inset: s * 0.14,
              borderRadius: "50%",
              background: "var(--surface-raised)",
            }}
          />
          {src ? (
            <img src={src} alt="" width={s} height={s} style={{ position: "relative" }} />
          ) : (
            <ImageIcon size={large ? 40 : 28} style={{ color: "var(--text-faint)" }} />
          )}
        </span>
      );
    }
    case "trace_style": {
      const w = large ? 180 : 110;
      return (
        <svg width={w} height={large ? 44 : 32} viewBox="0 0 110 32" aria-hidden="true">
          {m.glow && (
            <path d="M6 24 C 32 6, 66 28, 104 10" stroke="var(--gold-400)" strokeWidth="9" fill="none" strokeLinecap="round" opacity="0.3" />
          )}
          <path
            d="M6 24 C 32 6, 66 28, 104 10"
            stroke="var(--gold-400)"
            strokeWidth="3.5"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={m.dashArray ? m.dashArray.map((n) => n * 4).join(" ") : undefined}
          />
        </svg>
      );
    }
    case "stamp": {
      const src = getStampAsset(m.assetKey);
      const s = large ? 96 : 60;
      return src ? (
        <img src={src} alt="" width={s} height={s} />
      ) : (
        <ImageIcon size={large ? 40 : 28} style={{ color: "var(--text-faint)" }} />
      );
    }
    case "pin": {
      const s = large ? 34 : 22;
      return (
        <span
          style={{
            width: s,
            height: s,
            borderRadius: "50%",
            background: m.color ?? "var(--gold-400)",
            border: "3px solid rgba(255,255,255,0.25)",
            display: "inline-block",
          }}
        />
      );
    }
    case "physical":
      return item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt=""
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      ) : (
        <Package size={large ? 40 : 28} style={{ color: "var(--text-faint)" }} />
      );
    case "bundle":
      if (item.metadata.grantsWaypoints != null) {
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7, color: "var(--gold-400)" }}>
            <Gift size={large ? 40 : 28} />
            <WaypointsPrice amount={item.metadata.grantsWaypoints} size={large ? 13 : 11} />
          </div>
        );
      }
      return <Gift size={large ? 40 : 28} style={{ color: "var(--gold-400)" }} />;
  }
}

// Banner previewed in-situ on a fake profile header mock.
function BannerInSituPreview({ item }: { item: StoreItem }) {
  const src = getBannerAsset(item.metadata.assetKey);
  return (
    <div
      style={{
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        border: "1px solid var(--border-subtle)",
        background: "var(--surface-sunken)",
      }}
    >
      {src ? (
        <img src={src} alt="" style={{ width: "100%", height: 108, objectFit: "cover", display: "block" }} />
      ) : (
        <div
          style={{
            height: 108,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-faint)",
          }}
        >
          <ImageIcon size={32} />
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 12,
          padding: "0 16px 14px",
          marginTop: -22,
          position: "relative",
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "var(--radius-full)",
            background: "var(--surface-raised)",
            border: "3px solid var(--surface-card)",
            flexShrink: 0,
          }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 4 }}>
          <div style={{ width: 96, height: 10, borderRadius: "var(--radius-pill)", background: "var(--surface-raised)" }} />
          <div style={{ width: 64, height: 7, borderRadius: "var(--radius-pill)", background: "var(--surface-sunken)" }} />
        </div>
      </div>
    </div>
  );
}

// ─── In-situ purchase previews ────────────────────────────────────────────────
// "What will this actually look like?" popup, opened from the item detail
// modal. Each kind renders a mock of the surface it lives on, composed with
// the signed-in user's real name/avatar and currently equipped cosmetics
// (GET /profile/stats, same query key the profile page uses).

const PREVIEW_CTA: Record<StoreItemKind, string> = {
  badge: "See it on your profile",
  banner: "See it on your profile",
  title: "See it on your profile",
  frame: "See it on your profile",
  map_theme: "See it on your map",
  trace_style: "See it on your map",
  pin: "See it on your map",
  stamp: "See it on a check-in",
  award: "See it on a post",
  physical: "See it up close",
  bundle: "See what's inside",
};

// A store item viewed as if it were equipped — ProfileIdentity and friends
// take the resolved EquippedCosmeticItem shape.
const asEquipped = (item: StoreItem): EquippedCosmeticItem => ({
  itemId: item.id,
  slug: item.slug,
  name: item.name,
  metadata: item.metadata,
  rarity: item.rarity,
});

// Renders a real map marker (same canvas painter the product map registers
// with MapLibre) as an <img>, so the pin preview is pixel-identical.
function MarkerDot({
  color,
  cssSize,
  glyph,
  style,
}: {
  color: string;
  cssSize: number;
  glyph: MarkerGlyph;
  style?: React.CSSProperties;
}) {
  const src = React.useMemo(() => {
    if (typeof document === "undefined") return null;
    const img = createMarkerImage(color, cssSize, glyph);
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext("2d")!.putImageData(img, 0, 0);
    return canvas.toDataURL();
  }, [color, cssSize, glyph]);
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      width={cssSize}
      height={cssSize}
      style={{ display: "block", maxWidth: "none", ...style }}
    />
  );
}

// Profile header mock — the ProfilePage header at postcard size, with the
// previewed item swapped in over whatever is currently equipped.
function ProfileHeaderMock({
  name,
  avatarUrl,
  banner,
  title,
  badges,
  frame,
}: {
  name: string;
  avatarUrl: string | null | undefined;
  banner: EquippedCosmeticItem | null;
  title: EquippedCosmeticItem | null;
  badges: EquippedCosmeticItem[];
  frame: EquippedCosmeticItem | null;
}) {
  const bannerSrc = getBannerAsset(banner?.metadata.assetKey);
  return (
    <div
      className={bannerSrc ? undefined : "atlas-topo"}
      style={{
        position: "relative",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        border: "1px solid var(--border-subtle)",
        padding: bannerSrc ? "64px 18px 16px" : "28px 18px 18px",
        background: "var(--surface-sunken)",
        ...(bannerSrc
          ? {
              backgroundImage: `url("${bannerSrc}")`,
              backgroundSize: "cover",
              // Banner art keeps its horizon in the bottom third (same crop
              // rule as the real profile header).
              backgroundPosition: "center bottom",
              boxShadow: banner ? rarityGlowMd(banner.rarity) : undefined,
            }
          : {}),
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: bannerSrc
            ? "linear-gradient(0deg, color-mix(in srgb, var(--bg-app) 82%, transparent) 0%, color-mix(in srgb, var(--bg-app) 35%, transparent) 45%, transparent 75%)"
            : "linear-gradient(0deg, var(--bg-app) 0%, transparent 100%)",
        }}
      />
      <div style={{ position: "relative" }}>
        <ProfileIdentity
          name={name}
          avatarUrl={avatarUrl}
          title={title}
          badges={badges}
          frame={frame}
          size="md"
        />
      </div>
    </div>
  );
}

// Map mock — stylised fells with activity traces and real marker pins, styled
// by the merged map cosmetics (previewed item over currently equipped ones).
function MapMock({
  heatRamp,
  traceColor,
  dashArray,
  glow,
  pinColor,
}: {
  heatRamp?: string[] | null;
  traceColor?: string | null;
  dashArray?: number[] | null;
  glow?: boolean;
  pinColor?: string | null;
}) {
  const ramp = heatRamp && heatRamp.length === 5 ? heatRamp : null;
  const flat = traceColor ?? MAP_COLORS.gold;
  // Three traces, oldest → newest, sampled off the 5-stop recency ramp the
  // real heatmap layer interpolates over.
  const traceColors = ramp ? [ramp[1], ramp[3], ramp[4]] : [flat, flat, flat];
  const dash = dashArray ? dashArray.map((n) => n * 4).join(" ") : undefined;
  const pin = pinColor ?? MAP_COLORS.gold;
  const traces = [
    "M-10 60 C 70 78, 130 34, 200 56 S 310 28, 370 46",
    "M-10 155 C 50 128, 110 166, 170 130 S 290 82, 370 96",
    "M30 200 C 80 158, 170 176, 230 140 S 330 116, 370 126",
  ];
  return (
    <div
      style={{
        position: "relative",
        height: 200,
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        border: "1px solid var(--border-subtle)",
        background: "var(--surface-sunken)",
      }}
    >
      <svg
        viewBox="0 0 360 200"
        preserveAspectRatio="xMidYMid slice"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        aria-hidden="true"
      >
        {/* Contours + a tarn, so it reads as terrain. */}
        <g fill="none" stroke="var(--border-subtle)" strokeWidth="1">
          <ellipse cx="250" cy="52" rx="82" ry="34" />
          <ellipse cx="250" cy="52" rx="56" ry="22" />
          <ellipse cx="250" cy="52" rx="30" ry="11" />
          <ellipse cx="80" cy="150" rx="70" ry="30" />
          <ellipse cx="80" cy="150" rx="44" ry="18" />
        </g>
        <path
          d="M18 78 C 34 64, 66 66, 74 82 S 52 108, 32 100 S 6 90, 18 78 Z"
          fill={MAP_COLORS.sky}
          fillOpacity="0.14"
          stroke={MAP_COLORS.sky}
          strokeOpacity="0.3"
          strokeWidth="1"
        />
        {traces.map((d, i) =>
          glow ? (
            <path
              key={`g${i}`}
              d={d}
              stroke={traceColors[i]}
              strokeWidth="9"
              fill="none"
              strokeLinecap="round"
              opacity="0.3"
            />
          ) : null,
        )}
        {traces.map((d, i) => (
          <path
            key={i}
            d={d}
            stroke={traceColors[i]}
            strokeWidth="3.5"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={dash}
          />
        ))}
      </svg>
      {/* Real canvas-drawn markers: two collected peaks, a collected landmark,
          and one not-yet-collected peak (always grey) for contrast. */}
      <MarkerDot
        color={pin}
        cssSize={PEAK_MARKER_PX}
        glyph={PEAK_GLYPH}
        style={{ position: "absolute", left: "64%", top: "18%" }}
      />
      <MarkerDot
        color={pin}
        cssSize={PEAK_MARKER_PX}
        glyph={PEAK_GLYPH}
        style={{ position: "absolute", left: "16%", top: "62%" }}
      />
      <MarkerDot
        color={pin}
        cssSize={LANDMARK_MARKER_PX}
        glyph={LANDMARK_GLYPHS["waterfall"]!}
        style={{ position: "absolute", left: "44%", top: "44%" }}
      />
      <MarkerDot
        color={MAP_COLORS.grey}
        cssSize={PEAK_MARKER_PX}
        glyph={PEAK_GLYPH}
        style={{ position: "absolute", left: "84%", top: "58%" }}
      />
    </div>
  );
}

// Check-in mock — a photo stand-in with the stamp where check-in stamps sit
// (bottom-left, same size treatment as LandmarkDetailPage's gallery).
function CheckInMock({ stamp }: { stamp: StoreItem }) {
  const src = getStampAsset(stamp.metadata.assetKey);
  return (
    <div
      style={{
        position: "relative",
        aspectRatio: "4 / 3",
        maxWidth: 320,
        margin: "0 auto",
        borderRadius: "var(--radius-md)",
        overflow: "hidden",
        border: "1px solid var(--border-subtle)",
        background:
          "linear-gradient(180deg, color-mix(in srgb, var(--sky-400) 26%, var(--surface-sunken)) 0%, color-mix(in srgb, var(--spruce-400) 16%, var(--surface-sunken)) 62%, var(--surface-sunken) 100%)",
      }}
    >
      <svg
        viewBox="0 0 24 24"
        style={{
          position: "absolute",
          left: "50%",
          bottom: -10,
          width: "88%",
          transform: "translateX(-50%)",
          opacity: 0.18,
        }}
        aria-hidden="true"
      >
        <path d="M8 3 L12 12 L17 7 L22 22 L2 22 Z" fill="var(--ink-950)" />
      </svg>
      {src ? (
        <img
          src={src}
          alt=""
          style={{
            position: "absolute",
            bottom: 8,
            left: 8,
            width: 56,
            height: 56,
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
          }}
        />
      ) : (
        <ImageIcon
          size={28}
          style={{ position: "absolute", bottom: 10, left: 10, color: "var(--text-faint)" }}
        />
      )}
    </div>
  );
}

// Award mock — a post footer with the award chip as PostCard's AwardRow
// renders it once someone has given one.
function AwardMock({
  item,
  name,
  avatarUrl,
}: {
  item: StoreItem;
  name: string;
  avatarUrl: string | null | undefined;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: 14,
        borderRadius: "var(--radius-lg)",
        border: "1px solid var(--border-subtle)",
        background: "var(--surface-sunken)",
      }}
    >
      <ProfileIdentity name={name} avatarUrl={avatarUrl} size="sm" />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ width: "72%", height: 9, borderRadius: "var(--radius-pill)", background: "var(--surface-raised)" }} />
        <div style={{ width: "46%", height: 9, borderRadius: "var(--radius-pill)", background: "var(--surface-raised)" }} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 9px",
            borderRadius: "var(--radius-pill)",
            background: "var(--accent-soft)",
            border: "1px solid var(--border-gold)",
            color: "var(--gold-300)",
          }}
        >
          {awardIcon(item.metadata.icon, 13)}
          <span className="tnum" style={{ fontSize: 12, fontWeight: 700 }}>1</span>
        </span>
      </div>
    </div>
  );
}

// Bundle mock — the granted items laid out as mini tiles.
function BundleMock({ item, allItems }: { item: StoreItem; allItems: StoreItem[] }) {
  if (item.metadata.grantsWaypoints != null) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          padding: "28px 18px",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border-subtle)",
          background: "var(--surface-sunken)",
          color: "var(--gold-400)",
        }}
      >
        <Gift size={40} />
        <WaypointsPrice amount={item.metadata.grantsWaypoints} size={15} />
      </div>
    );
  }
  const granted = item.metadata.grantsItemSlugs ?? [];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
      {item.metadata.grantsLifetimePro && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            alignItems: "center",
            justifyContent: "center",
            padding: 12,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-gold)",
            background: "var(--surface-sunken)",
            color: "var(--gold-400)",
          }}
        >
          <Zap size={24} />
          <span style={{ fontSize: 11.5, fontWeight: 600 }}>Lifetime Pro</span>
        </div>
      )}
      {granted.map((slug) => {
        const match = allItems.find((i) => i.slug === slug);
        return (
          <div
            key={slug}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              alignItems: "center",
              padding: 12,
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-subtle)",
              background: "var(--surface-sunken)",
            }}
          >
            <div
              style={{
                height: 56,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {match ? (
                <ItemPreview item={match} />
              ) : (
                <Gift size={24} style={{ color: "var(--gold-400)" }} />
              )}
            </div>
            <span style={{ fontSize: 11.5, color: "var(--text-secondary)", textAlign: "center" }}>
              {match?.name ?? prettySlug(slug)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ItemInSituPreviewModal({
  item,
  allItems,
  onClose,
}: {
  item: StoreItem;
  allItems: StoreItem[];
  onClose: () => void;
}) {
  const { user } = useAuth();
  const profileQuery = useQuery({
    queryKey: ["profile/stats"],
    queryFn: fetchProfileStats,
    staleTime: 60_000,
    retry: false,
  });
  const eq = profileQuery.data?.equipped;
  const name = user?.name ?? user?.username ?? "Explorer";
  const preview = asEquipped(item);

  let body: React.ReactNode;
  let caption: string;
  switch (item.kind) {
    case "banner":
    case "title":
    case "frame":
    case "badge": {
      body = (
        <ProfileHeaderMock
          name={name}
          avatarUrl={user?.avatarUrl}
          banner={item.kind === "banner" ? preview : eq?.banner ?? null}
          title={item.kind === "title" ? preview : eq?.title ?? null}
          frame={item.kind === "frame" ? preview : eq?.frame ?? null}
          badges={
            item.kind === "badge"
              ? [preview, ...(eq?.badges ?? []).filter((b) => b.slug !== item.slug)].slice(0, 3)
              : eq?.badges ?? []
          }
        />
      );
      caption = "Your profile header with it equipped, alongside what you have on now.";
      break;
    }
    case "map_theme":
    case "trace_style":
    case "pin": {
      const theme = item.kind === "map_theme" ? item.metadata : eq?.mapTheme?.metadata;
      const trace = item.kind === "trace_style" ? item.metadata : eq?.traceStyle?.metadata;
      const pinColor = item.kind === "pin" ? item.metadata.color : eq?.pin?.metadata.color;
      body = (
        <MapMock
          heatRamp={theme?.heatRamp}
          traceColor={theme?.traceColor}
          dashArray={trace?.dashArray}
          glow={trace?.glow === true}
          pinColor={pinColor ?? null}
        />
      );
      caption =
        "Your traces and collected pins with it equipped, combined with your other map styles. Pins you haven't collected stay grey.";
      break;
    }
    case "stamp":
      body = <CheckInMock stamp={item} />;
      caption = "Stamped on every check-in photo you post while it's equipped.";
      break;
    case "award":
      // Awards land on someone else's post, so the mock author is generic.
      body = <AwardMock item={item} name="Another explorer" avatarUrl={null} />;
      caption = "Given to someone else's post - its Waypoints pass to the author.";
      break;
    case "physical":
      body = item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt={item.name}
          style={{
            width: "100%",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--border-subtle)",
            display: "block",
          }}
        />
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 160,
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--border-subtle)",
            background: "var(--surface-sunken)",
            color: "var(--text-faint)",
          }}
        >
          <Package size={40} />
        </div>
      );
      caption = "Posted to you - shipping details are collected at checkout.";
      break;
    case "bundle":
      body = <BundleMock item={item} allItems={allItems} />;
      caption =
        item.metadata.grantsWaypoints != null
          ? "Sent as a gift to another explorer at checkout."
          : "Everything the bundle unlocks in one go.";
      break;
  }

  return (
    <ModalShell onClose={onClose} maxWidth={520}>
      <div className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Preview</div>
      <h2
        style={{
          font: "var(--type-h3)",
          color: "var(--text-primary)",
          marginBottom: 14,
          paddingRight: 28,
        }}
      >
        {item.name}
      </h2>
      {body}
      <p
        style={{
          fontSize: 12.5,
          color: "var(--text-muted)",
          lineHeight: 1.5,
          marginTop: 12,
          marginBottom: 0,
        }}
      >
        {caption}
      </p>
    </ModalShell>
  );
}

// ─── Price row (tile) ─────────────────────────────────────────────────────────

function PriceRow({ item }: { item: StoreItem }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {item.priceWaypoints != null && <WaypointsPrice amount={item.priceWaypoints} />}
      {item.priceWaypoints != null && item.pricePence != null && (
        <span style={{ color: "var(--text-faint)", fontSize: 11 }}>or</span>
      )}
      {item.pricePence != null && (
        <span
          className="tnum"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            fontWeight: 700,
            color: "var(--text-secondary)",
          }}
        >
          {gbp(item.pricePence)}
        </span>
      )}
    </div>
  );
}

function RarityChip({ item }: { item: StoreItem }) {
  if (item.rarity === "common") return null;
  return (
    <span
      className="eyebrow"
      style={{ fontSize: 9, flexShrink: 0, color: rarityColor(item.rarity) }}
    >
      {rarityLabel(item.rarity)}
    </span>
  );
}

function OwnedBadge({ item }: { item: StoreItem }) {
  if (!item.owned) return null;
  return (
    <Badge variant="success" icon={<Check size={12} />}>
      {item.kind === "award" ? `×${item.ownedQuantity} owned` : "Owned"}
    </Badge>
  );
}

// ─── Store tile ───────────────────────────────────────────────────────────────

function StoreTile({ item, onClick }: { item: StoreItem; onClick: () => void }) {
  const cls = [
    "atlas-store-tile",
    item.featured ? "atlas-store-tile--featured" : "",
    item.locked ? "atlas-store-tile--locked" : "",
    item.rarity !== "common" ? `atlas-store-tile--${item.rarity}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type="button" className={cls} onClick={onClick} title={item.name}>
      <div className="atlas-store-tile__preview">
        <ItemPreview item={item} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: item.locked ? "var(--text-muted)" : "var(--text-primary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.name}
          </span>
          <span className="eyebrow" style={{ fontSize: 9, flexShrink: 0 }}>
            {KIND_LABEL[item.kind]}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 22 }}>
          <PriceRow item={item} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <RarityChip item={item} />
            <OwnedBadge item={item} />
          </div>
        </div>
        {item.locked && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11.5,
              color: "var(--text-muted)",
              lineHeight: 1.4,
            }}
          >
            <Lock size={12} style={{ flexShrink: 0 }} />
            {item.lockedReason ?? "Locked"}
          </div>
        )}
        {!item.locked && item.availableUntil && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11.5,
              color: "var(--gold-300)",
              lineHeight: 1.4,
            }}
          >
            <Clock size={12} style={{ flexShrink: 0 }} />
            {leavesStoreCopy(item.availableUntil)}
          </div>
        )}
      </div>
    </button>
  );
}

// ─── Featured card ────────────────────────────────────────────────────────────

function FeaturedCard({
  item,
  allItems,
  onClick,
}: {
  item: StoreItem;
  allItems: StoreItem[];
  onClick: () => void;
}) {
  const isBundle = item.kind === "bundle";
  const granted = item.metadata.grantsItemSlugs ?? [];

  return (
    <button
      type="button"
      onClick={onClick}
      title={item.name}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 12,
        textAlign: "left",
        cursor: "pointer",
        padding: 18,
        borderRadius: "var(--radius-lg)",
        border: "1px solid var(--border-gold)",
        background: "linear-gradient(160deg, var(--accent-soft), transparent 70%), var(--surface-card)",
        boxShadow: "var(--shadow-sm), var(--ring-top)",
        fontFamily: "var(--font-sans)",
        color: "var(--text-primary)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span className="eyebrow" style={{ fontSize: 10, color: "var(--gold-400)" }}>
          Featured · {KIND_LABEL[item.kind]}
          {item.rarity !== "common" ? ` · ${rarityLabel(item.rarity)}` : ""}
        </span>
        {item.metadata.grantsLifetimePro && (
          <Badge variant="gold" icon={<Zap size={11} />}>
            Lifetime Pro
          </Badge>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div
          style={{
            width: 72,
            height: 72,
            flexShrink: 0,
            borderRadius: "var(--radius-md)",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            padding: 8,
          }}
        >
          <ItemPreview item={item} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{item.name}</div>
          <div
            style={{
              fontSize: 12.5,
              color: "var(--text-secondary)",
              lineHeight: 1.45,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {item.description}
          </div>
        </div>
      </div>

      {isBundle && granted.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {granted.map((slug) => {
            const match = allItems.find((i) => i.slug === slug);
            return (
              <Badge key={slug} variant="neutral">
                {match?.name ?? prettySlug(slug)}
              </Badge>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          {item.pricePence != null && (
            <span
              className="tnum"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 22,
                fontWeight: 700,
                color: "var(--text-accent)",
              }}
            >
              {gbp(item.pricePence)}
            </span>
          )}
          {item.priceWaypoints != null && (
            <WaypointsPrice amount={item.priceWaypoints} size={13} />
          )}
          {isBundle && (
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>one-off</span>
          )}
        </div>
        <OwnedBadge item={item} />
      </div>
    </button>
  );
}

// ─── Explainer modal ──────────────────────────────────────────────────────────

function ModalShell({
  onClose,
  children,
  maxWidth = 420,
}: {
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: number;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--scrim)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="atlas-store-modal"
        role="dialog"
        aria-modal="true"
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 24,
          maxWidth,
          width: "100%",
          maxHeight: "88vh",
          overflow: "auto",
          boxShadow: "var(--shadow-xl)",
          position: "relative",
        }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            width: 28,
            height: 28,
            display: "grid",
            placeItems: "center",
            borderRadius: "var(--radius-sm)",
            border: "none",
            background: "transparent",
            color: "var(--text-muted)",
            cursor: "pointer",
          }}
        >
          <X size={16} />
        </button>
        {children}
      </div>
    </div>
  );
}

function WaypointsExplainerModal({ onClose }: { onClose: () => void }) {
  const rules = [
    "Levelling up grants your new level ×50 Waypoints for each level gained — reaching level 5 earns 250.",
    "Awards from other explorers pass on Waypoints — when someone gives your post an award, its Waypoints go to you.",
    "Waypoints are separate from your Outdoor Score and never expire.",
  ];
  return (
    <ModalShell onClose={onClose}>
      <div className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Rewards</div>
      <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 14 }}>
        How Waypoints work
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {rules.map((r) => (
          <div key={r} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ color: "var(--gold-400)", flexShrink: 0, marginTop: 3 }}>
              <Triangle size={11} fill="currentColor" aria-hidden="true" />
            </span>
            <p style={{ fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.55, margin: 0 }}>
              {r}
            </p>
          </div>
        ))}
      </div>
    </ModalShell>
  );
}

// ─── Item detail modal ────────────────────────────────────────────────────────

function ItemDetailModal({
  item,
  allItems,
  wallet,
  onClose,
}: {
  item: StoreItem;
  allItems: StoreItem[];
  wallet: number | undefined;
  onClose: () => void;
}) {
  const betaGate = useBetaGate();
  const queryClient = useQueryClient();
  const [purchased, setPurchased] = React.useState(false);
  const [betaGateOpen, setBetaGateOpen] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const isGiftPack = item.kind === "bundle" && item.metadata.grantsWaypoints != null;
  const [giftToUsername, setGiftToUsername] = React.useState("");
  const [giftMessage, setGiftMessage] = React.useState("");

  const purchaseMutation = useMutation({
    mutationFn: () =>
      purchaseItem({ itemSlug: item.slug, currency: "waypoints", quantity: 1 }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["economy/wallet"] });
      const prev = queryClient.getQueryData<WalletResponse>(["economy/wallet"]);
      if (prev && item.priceWaypoints != null) {
        queryClient.setQueryData<WalletResponse>(["economy/wallet"], {
          ...prev,
          waypoints: prev.waypoints - item.priceWaypoints,
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["economy/wallet"], ctx.prev);
    },
    onSuccess: () => {
      setPurchased(true);
      void queryClient.invalidateQueries({ queryKey: ["economy/wallet"] });
      void queryClient.invalidateQueries({ queryKey: ["store/items"] });
      void queryClient.invalidateQueries({ queryKey: ["store/inventory"] });
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: () =>
      createStoreCheckout({
        items: [{ slug: item.slug, quantity: 1 }],
        ...(isGiftPack
          ? {
              giftToUsername: giftToUsername.trim().replace(/^@/, ""),
              ...(giftMessage.trim() ? { giftMessage: giftMessage.trim() } : {}),
            }
          : {}),
      }),
    onSuccess: ({ url }) => {
      if (url) window.location.assign(url);
    },
  });

  const alreadyOwned = item.owned && item.kind !== "award" && !isGiftPack;
  const canBuyWaypoints = item.priceWaypoints != null && !item.locked && !alreadyOwned && !purchased;
  const canBuyCard =
    item.pricePence != null &&
    !item.locked &&
    !alreadyOwned &&
    !purchased &&
    (!isGiftPack || giftToUsername.trim().replace(/^@/, "").length > 0);
  const insufficient =
    item.priceWaypoints != null && wallet != null && wallet < item.priceWaypoints;
  const gap = item.priceWaypoints != null && wallet != null ? item.priceWaypoints - wallet : 0;
  const cardIsPrimary = item.priceWaypoints == null;

  const error =
    (purchaseMutation.isError
      ? errorMessage(purchaseMutation.error, "Couldn't complete that purchase. Try again.")
      : null) ??
    (checkoutMutation.isError
      ? errorMessage(checkoutMutation.error, "Couldn't start checkout. Try again.")
      : null);

  return (
    <ModalShell onClose={onClose} maxWidth={460}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <div className="eyebrow" style={{ fontSize: 10 }}>
          {KIND_LABEL[item.kind]}
        </div>
        <RarityChip item={item} />
      </div>
      <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 14, paddingRight: 28 }}>
        {item.name}
      </h2>

      {/* Preview */}
      <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 4 }}>
        {item.kind === "banner" ? (
          <BannerInSituPreview item={item} />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 120,
              padding: 18,
              borderRadius: "var(--radius-lg)",
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-subtle)",
              boxShadow: item.locked ? undefined : rarityGlowMd(item.rarity),
              filter: item.locked ? "grayscale(1)" : undefined,
              opacity: item.locked ? 0.6 : 1,
            }}
          >
            <ItemPreview item={item} large />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Eye size={13} />}
            onClick={() => setPreviewOpen(true)}
          >
            {PREVIEW_CTA[item.kind]}
          </Button>
        </div>
      </div>

      {item.description && (
        <p style={{ fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.55, marginBottom: 16 }}>
          {item.description}
        </p>
      )}

      {/* Bundle contents */}
      {item.kind === "bundle" && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          {item.metadata.grantsLifetimePro && (
            <Badge variant="gold" icon={<Zap size={11} />}>
              Lifetime Pro
            </Badge>
          )}
          {(item.metadata.grantsItemSlugs ?? []).map((slug) => (
            <Badge key={slug} variant="neutral">
              {prettySlug(slug)}
            </Badge>
          ))}
        </div>
      )}

      {/* Price row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "12px 14px",
          borderRadius: "var(--radius-md)",
          background: "var(--surface-sunken)",
          border: "1px solid var(--border-subtle)",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {item.priceWaypoints != null && <WaypointsPrice amount={item.priceWaypoints} size={15} />}
          {item.priceWaypoints != null && item.pricePence != null && (
            <span style={{ color: "var(--text-faint)", fontSize: 12 }}>or</span>
          )}
          {item.pricePence != null && (
            <span
              className="tnum"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 15,
                fontWeight: 700,
                color: "var(--text-primary)",
              }}
            >
              {gbp(item.pricePence)}
            </span>
          )}
        </div>
        <OwnedBadge item={item} />
      </div>

      {/* Locked */}
      {item.locked && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "var(--text-muted)",
            marginBottom: 16,
          }}
        >
          <Lock size={14} style={{ flexShrink: 0 }} />
          {item.lockedReason ?? "Locked"}
        </div>
      )}

      {/* Seasonal window */}
      {!item.locked && item.availableUntil && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "var(--gold-300)",
            marginBottom: 16,
          }}
        >
          <Clock size={14} style={{ flexShrink: 0 }} />
          {leavesStoreCopy(item.availableUntil)}
        </div>
      )}

      {/* Gift recipient */}
      {isGiftPack && !item.locked && !purchased && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span className="eyebrow" style={{ fontSize: 10 }}>Send to</span>
            <input
              type="text"
              value={giftToUsername}
              onChange={(e) => setGiftToUsername(e.target.value)}
              placeholder="Their username"
              autoComplete="off"
              spellCheck={false}
              style={{
                padding: "9px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-subtle)",
                background: "var(--surface-sunken)",
                color: "var(--text-primary)",
                fontFamily: "var(--font-mono)",
                fontSize: 13,
              }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span className="eyebrow" style={{ fontSize: 10 }}>Note (optional)</span>
            <input
              type="text"
              value={giftMessage}
              maxLength={200}
              onChange={(e) => setGiftMessage(e.target.value)}
              placeholder="For that inversion photo"
              style={{
                padding: "9px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-subtle)",
                background: "var(--surface-sunken)",
                color: "var(--text-primary)",
                fontFamily: "var(--font-sans)",
                fontSize: 13,
              }}
            />
          </label>
        </div>
      )}

      {/* Success */}
      {purchased && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            borderRadius: "var(--radius-md)",
            background: "var(--success-soft)",
            border: "1px solid var(--status-success)",
            fontSize: 13,
            color: "var(--spruce-300)",
            marginBottom: 16,
          }}
        >
          <Check size={14} />
          Purchase complete — it&apos;s in your inventory
        </div>
      )}

      {/* Error from server (409 / 403) */}
      {error && !purchased && (
        <p style={{ fontSize: 13, color: "var(--status-danger)", marginBottom: 12 }}>{error}</p>
      )}

      {/* Actions */}
      {!item.locked && !alreadyOwned && !purchased && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {item.priceWaypoints != null && (
            <>
              <Button
                variant="primary"
                block
                leftIcon={<Triangle size={13} fill="currentColor" aria-hidden="true" />}
                disabled={!canBuyWaypoints || insufficient || purchaseMutation.isPending}
                onClick={() => purchaseMutation.mutate()}
              >
                {purchaseMutation.isPending ? "Buying…" : "Buy with Waypoints"}
              </Button>
              {insufficient && wallet != null && (
                <p
                  className="tnum"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11.5,
                    color: "var(--text-muted)",
                    textAlign: "center",
                    margin: 0,
                  }}
                >
                  You have {fmt(wallet)} · {fmt(gap)} more to go
                </p>
              )}
            </>
          )}
          {item.pricePence != null && (
            <Button
              variant={cardIsPrimary ? "primary" : "secondary"}
              block
              leftIcon={isGiftPack ? <Gift size={15} /> : <CreditCard size={15} />}
              disabled={!canBuyCard || checkoutMutation.isPending}
              onClick={() => (betaGate ? setBetaGateOpen(true) : checkoutMutation.mutate())}
            >
              {checkoutMutation.isPending
                ? "Opening checkout…"
                : isGiftPack
                  ? `Send gift · ${gbp(item.pricePence)}`
                  : `Buy with card · ${gbp(item.pricePence)}`}
            </Button>
          )}
        </div>
      )}

      {betaGateOpen && <BetaGateModal onClose={() => setBetaGateOpen(false)} />}
      {previewOpen && (
        <ItemInSituPreviewModal
          item={item}
          allItems={allItems}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </ModalShell>
  );
}

// ─── StorePage ────────────────────────────────────────────────────────────────

export default function StorePage() {
  ensureCss();
  const [filter, setFilter] = React.useState<"all" | "map_kinds" | StoreItemKind>("all");
  const [explainerOpen, setExplainerOpen] = React.useState(false);
  const [selectedSlug, setSelectedSlug] = React.useState<string | null>(null);

  const search = useSearch({ from: "/_protected/store" });

  const walletQuery = useQuery({
    queryKey: ["economy/wallet"],
    queryFn: getWallet,
  });

  const itemsQuery = useQuery({
    queryKey: ["store/items"],
    queryFn: getStoreItems,
  });

  const items = React.useMemo(
    () =>
      [...(itemsQuery.data?.items ?? [])].sort(
        (a, b) =>
          kindRank(a.kind) - kindRank(b.kind) ||
          rarityRank(a.rarity) - rarityRank(b.rarity) ||
          a.sortOrder - b.sortOrder,
      ),
    [itemsQuery.data],
  );

  const featured = items.filter((i) => i.featured);
  const gridItems = items.filter((i) =>
    filter === "all"
      ? !i.featured
      : filter === "map_kinds"
        ? MAP_KINDS.includes(i.kind)
        : i.kind === filter,
  );

  const selected = selectedSlug ? items.find((i) => i.slug === selectedSlug) ?? null : null;
  const wallet = walletQuery.data?.waypoints;
  const isLoading = itemsQuery.isLoading;

  return (
    <div
      className="r-page"
      style={{
        padding: 28,
        overflow: "auto",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 20,
          flexWrap: "wrap",
        }}
      >
        <div>
          <p className="eyebrow" style={{ fontSize: 11, marginBottom: 4 }}>Rewards</p>
          <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>
            Store
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 6, marginBottom: 0 }}>
            Spend your Waypoints on badges, banners, titles, frames, stamps and map styles.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 24 }}>
            {walletQuery.isLoading ? (
              <Skel width="150px" height="20px" />
            ) : wallet != null ? (
              <>
                <Triangle
                  size={14}
                  fill="currentColor"
                  aria-hidden="true"
                  style={{ color: "var(--gold-400)" }}
                />
                <span
                  className="tnum"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 19,
                    fontWeight: 700,
                    color: "var(--gold-400)",
                  }}
                >
                  {fmt(wallet)}
                </span>
                <span className="eyebrow" style={{ fontSize: 10, color: "var(--gold-400)" }}>
                  Waypoints
                </span>
              </>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setExplainerOpen(true)}
            style={{
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              fontSize: 12,
              color: "var(--text-muted)",
              textDecoration: "underline",
              textUnderlineOffset: 3,
              fontFamily: "var(--font-sans)",
            }}
          >
            How Waypoints work
          </button>
        </div>
      </div>

      {/* ── Checkout return banners ─────────────────────────────────────── */}
      {search.status === "success" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px",
            background: "var(--success-soft)",
            border: "1px solid var(--status-success)",
            borderRadius: "var(--radius-md)",
            fontSize: 14,
            color: "var(--spruce-300)",
          }}
        >
          <Check size={16} />
          Purchase complete — it&apos;s in your inventory
        </div>
      )}
      {search.status === "cancelled" && (
        <div
          style={{
            padding: "12px 16px",
            background: "var(--surface-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
            fontSize: 14,
            color: "var(--text-secondary)",
          }}
        >
          Checkout cancelled — nothing was charged.
        </div>
      )}

      {/* ── Featured row ────────────────────────────────────────────────── */}
      {isLoading ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Skel height="150px" />
          <Skel height="150px" />
        </div>
      ) : featured.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: featured.length === 1 ? "minmax(0, 560px)" : "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 12,
          }}
        >
          {featured.map((item) => (
            <FeaturedCard
              key={item.id}
              item={item}
              allItems={items}
              onClick={() => setSelectedSlug(item.slug)}
            />
          ))}
        </div>
      ) : null}

      {/* ── Filter chips ────────────────────────────────────────────────── */}
      <div
        className="r-chip-scroll"
        style={{
          display: "flex",
          gap: 6,
          padding: 4,
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-pill)",
          alignSelf: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              style={{
                padding: "0 15px",
                height: 32,
                borderRadius: "var(--radius-pill)",
                cursor: "pointer",
                border: `1px solid ${active ? "var(--border-gold)" : "transparent"}`,
                fontFamily: "var(--font-sans)",
                fontSize: 13,
                fontWeight: 600,
                background: active ? "var(--accent-soft)" : "transparent",
                color: active ? "var(--text-accent)" : "var(--text-secondary)",
                transition: "var(--t-colors)",
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* ── Item grid ───────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="atlas-store-grid">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Skel key={i} height="196px" radius="var(--radius-lg)" />
          ))}
        </div>
      ) : gridItems.length === 0 ? (
        filter === "physical" ? (
          <EmptyState
            icon={<Package size={20} />}
            title="No merch yet"
            hint="Physical rewards are coming."
          />
        ) : (
          <EmptyState
            icon={<ShoppingBag size={20} />}
            title="Nothing here yet"
            hint="New items arrive with each release — check back soon."
          />
        )
      ) : (
        <div className="atlas-store-grid">
          {gridItems.map((item) => (
            <StoreTile key={item.id} item={item} onClick={() => setSelectedSlug(item.slug)} />
          ))}
        </div>
      )}

      {/* ── Recent Waypoints activity ───────────────────────────────────── */}
      {walletQuery.data && walletQuery.data.recent.length > 0 && (
        <AtlasPanel title="Recent Waypoints activity">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {walletQuery.data.recent.slice(0, 6).map((row) => (
              <div
                key={row.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  fontSize: 13,
                }}
              >
                <span style={{ color: "var(--text-secondary)" }}>
                  {row.reason === "level_up"
                    ? "Level up"
                    : row.reason === "store_purchase"
                      ? "Store purchase"
                      : row.reason === "award_received"
                        ? "Award received"
                        : row.reason === "initial_grant"
                          ? "Starting balance"
                          : row.reason === "admin_grant"
                            ? "Grant"
                            : row.reason === "gift_received"
                              ? "Gift received"
                              : row.reason === "pro_allowance"
                                ? "Pro monthly allowance"
                                : row.reason === "group_challenge_prize"
                                  ? "Challenge prize"
                                  : row.reason.replace(/_/g, " ")}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span
                    className="tnum"
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: row.delta >= 0 ? "var(--spruce-400)" : "var(--text-muted)",
                    }}
                  >
                    {row.delta >= 0 ? "+" : ""}
                    {fmt(row.delta)}
                  </span>
                  <span
                    className="tnum"
                    style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-faint)" }}
                  >
                    {new Date(row.createdAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </AtlasPanel>
      )}

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {explainerOpen && <WaypointsExplainerModal onClose={() => setExplainerOpen(false)} />}
      {selected && (
        <ItemDetailModal
          item={selected}
          allItems={items}
          wallet={wallet}
          onClose={() => setSelectedSlug(null)}
        />
      )}
    </div>
  );
}
